import type { ComponentType, SVGProps } from 'react'
import {
  HomeIcon,
  BuildingStorefrontIcon,
  BuildingLibraryIcon,
  UsersIcon,
  CubeIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  UserGroupIcon,
  TruckIcon,
  TagIcon,
  TicketIcon,
  ChartBarIcon,
  CogIcon,
  CurrencyDollarIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CalendarIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  ShoppingCartIcon,
  ClockIcon,
  MapPinIcon,
  ShareIcon,
  SparklesIcon,
  ArchiveBoxIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  NewspaperIcon,
  RectangleStackIcon,
  StarIcon,
} from '@heroicons/react/24/outline'

export type NavGroupKey =
  | 'daily'
  | 'catalog'
  | 'marketing'
  | 'website'
  | 'collaboration'
  | 'messaging'
  | 'finance'
  | 'shopSystem'
  | 'platform'

export const STAFF_NAV_GROUP_ORDER: NavGroupKey[] = [
  'daily',
  'catalog',
  'marketing',
  'website',
  'collaboration',
  'messaging',
  'finance',
  'shopSystem',
  'platform',
]

export const DEFAULT_EXPANDED_NAV_GROUPS: NavGroupKey[] = ['daily']

export interface NavigationItemConfig {
  nameKey: string
  href: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  /** Collapsible section; omit when `pinned` or `footer` is true. */
  groupKey?: NavGroupKey
  /** Always visible at top of the nav (not inside a collapsible group). */
  pinned?: boolean
  /** Shown at bottom of the sidebar, above Help (staff). */
  footer?: boolean
}

export const staffNavigationConfig: NavigationItemConfig[] = [
  { nameKey: 'dashboard', href: '/dashboard', icon: HomeIcon, pinned: true },
  { nameKey: 'orders', href: '/dashboard/orders', icon: ShoppingBagIcon, groupKey: 'daily' },
  { nameKey: 'quotations', href: '/dashboard/quotations', icon: DocumentTextIcon, groupKey: 'daily' },
  { nameKey: 'outboundShipments', href: '/dashboard/outbound-shipments', icon: ArchiveBoxIcon, groupKey: 'daily' },
  { nameKey: 'payments', href: '/dashboard/payments', icon: CreditCardIcon, groupKey: 'daily' },
  { nameKey: 'paymentIntents', href: '/dashboard/payments/intents', icon: CreditCardIcon, groupKey: 'daily' },
  { nameKey: 'customers', href: '/dashboard/customers', icon: UsersIcon, groupKey: 'daily' },
  { nameKey: 'schedule', href: '/dashboard/schedule', icon: CalendarIcon, groupKey: 'daily' },
  { nameKey: 'scheduleResources', href: '/dashboard/schedule/resources', icon: BuildingOffice2Icon, groupKey: 'daily' },
  { nameKey: 'classPackages', href: '/dashboard/class-packages', icon: UserGroupIcon, groupKey: 'daily' },
  { nameKey: 'products', href: '/dashboard/products', icon: CubeIcon, groupKey: 'catalog' },
  { nameKey: 'brands', href: '/dashboard/brands', icon: BuildingLibraryIcon, groupKey: 'catalog' },
  { nameKey: 'tags', href: '/dashboard/tags', icon: TagIcon, groupKey: 'catalog' },
  { nameKey: 'suppliers', href: '/dashboard/suppliers', icon: TruckIcon, groupKey: 'catalog' },
  { nameKey: 'purchaseOrders', href: '/dashboard/purchase-orders', icon: ShoppingCartIcon, groupKey: 'catalog' },
  { nameKey: 'subscription', href: '/dashboard/subscription', icon: CalendarDaysIcon, groupKey: 'marketing' },
  { nameKey: 'leases', href: '/dashboard/leases', icon: BuildingOffice2Icon, groupKey: 'daily' },
  { nameKey: 'events', href: '/dashboard/events', icon: CalendarDaysIcon, groupKey: 'marketing' },
  { nameKey: 'eventForms', href: '/dashboard/events/forms', icon: ClipboardDocumentListIcon, groupKey: 'marketing' },
  { nameKey: 'coupons', href: '/dashboard/coupons', icon: TicketIcon, groupKey: 'marketing' },
  { nameKey: 'companionBundles', href: '/dashboard/companion-bundles', icon: Squares2X2Icon, groupKey: 'marketing' },
  { nameKey: 'membership', href: '/dashboard/membership', icon: SparklesIcon, groupKey: 'marketing' },
  { nameKey: 'referrers', href: '/dashboard/referrers', icon: ShareIcon, groupKey: 'marketing' },
  { nameKey: 'socialPromote', href: '/dashboard/social-promote', icon: ShareIcon, groupKey: 'marketing' },
  { nameKey: 'webDesigner', href: '/dashboard/website', icon: GlobeAltIcon, groupKey: 'website' },
  { nameKey: 'websiteBlog', href: '/dashboard/website/blog', icon: NewspaperIcon, groupKey: 'website' },
  { nameKey: 'websiteTemplates', href: '/dashboard/website/templates', icon: RectangleStackIcon, groupKey: 'website' },
  { nameKey: 'websiteAnalytics', href: '/dashboard/website/analytics', icon: ChartBarIcon, groupKey: 'website' },
  { nameKey: 'websiteReviews', href: '/dashboard/website/reviews', icon: StarIcon, groupKey: 'website' },
  { nameKey: 'teams', href: '/dashboard/teams', icon: UserGroupIcon, groupKey: 'collaboration' },
  { nameKey: 'collaboration', href: '/dashboard/collaboration', icon: ClipboardDocumentListIcon, groupKey: 'collaboration' },
  { nameKey: 'teamFiles', href: '/dashboard/team-files', icon: FolderIcon, groupKey: 'collaboration' },
  { nameKey: 'messagingTemplates', href: '/dashboard/messaging/templates', icon: ChatBubbleLeftRightIcon, groupKey: 'messaging' },
  { nameKey: 'sendMessage', href: '/dashboard/messaging/send', icon: ChatBubbleLeftRightIcon, groupKey: 'messaging' },
  { nameKey: 'bookings', href: '/dashboard/bookings', icon: DocumentTextIcon, groupKey: 'finance' },
  { nameKey: 'staff', href: '/dashboard/staff', icon: UserGroupIcon, groupKey: 'finance' },
  { nameKey: 'staffTimeEntries', href: '/dashboard/staff-time-entries', icon: ClockIcon, groupKey: 'finance' },
  { nameKey: 'staffLeave', href: '/dashboard/staff-leave', icon: CalendarDaysIcon, groupKey: 'finance' },
  { nameKey: 'journal', href: '/dashboard/journal', icon: BookOpenIcon, groupKey: 'finance' },
  { nameKey: 'vendorBills', href: '/dashboard/vendor-bills', icon: DocumentTextIcon, groupKey: 'finance' },
  { nameKey: 'orderReturns', href: '/dashboard/order-returns', icon: ArchiveBoxIcon, groupKey: 'daily' },
  { nameKey: 'payroll', href: '/dashboard/payroll', icon: CurrencyDollarIcon, groupKey: 'finance' },
  { nameKey: 'reports', href: '/dashboard/reports', icon: ChartBarIcon, groupKey: 'finance' },
  { nameKey: 'shops', href: '/dashboard/shops', icon: BuildingStorefrontIcon, groupKey: 'shopSystem' },
  { nameKey: 'branches', href: '/dashboard/branches', icon: MapPinIcon, groupKey: 'shopSystem' },
  { nameKey: 'automationTasks', href: '/dashboard/automation-tasks', icon: ClockIcon, groupKey: 'shopSystem' },
  { nameKey: 'dataManagement', href: '/dashboard/data-management', icon: ArrowUpTrayIcon, groupKey: 'shopSystem' },
  { nameKey: 'settings', href: '/dashboard/settings', icon: CogIcon, footer: true },
  { nameKey: 'supportInbox', href: '/dashboard/support', icon: ChatBubbleLeftRightIcon, footer: true },
]

export const customerNavigationConfig: NavigationItemConfig[] = [
  { nameKey: 'dashboard', href: '/customer', icon: HomeIcon, pinned: true },
  { nameKey: 'products', href: '/customer/products', icon: CubeIcon, groupKey: 'daily' },
  { nameKey: 'cart', href: '/customer/cart', icon: ShoppingBagIcon, groupKey: 'daily' },
  { nameKey: 'orders', href: '/customer/orders', icon: ShoppingBagIcon, groupKey: 'daily' },
  { nameKey: 'downloads', href: '/customer/downloads', icon: ArrowDownTrayIcon, groupKey: 'daily' },
  { nameKey: 'tickets', href: '/customer/tickets', icon: TicketIcon, groupKey: 'daily' },
  { nameKey: 'coupons', href: '/customer/coupons', icon: TicketIcon, groupKey: 'marketing' },
  { nameKey: 'membership', href: '/customer/membership', icon: SparklesIcon, groupKey: 'marketing' },
  { nameKey: 'subscriptions', href: '/customer/subscriptions', icon: CalendarDaysIcon, groupKey: 'marketing' },
  { nameKey: 'schedule', href: '/customer/schedule', icon: CalendarIcon, groupKey: 'daily' },
  { nameKey: 'support', href: '/customer/support', icon: ChatBubbleLeftRightIcon, groupKey: 'shopSystem' },
  { nameKey: 'profile', href: '/customer/profile', icon: UsersIcon, groupKey: 'shopSystem' },
]

export const CUSTOMER_NAV_GROUP_ORDER: NavGroupKey[] = ['daily', 'marketing', 'shopSystem']

export function sidebarExpandedStorageKey(portal: 'staff' | 'customer'): string {
  return portal === 'customer'
    ? 'oms-sidebar-expanded-groups-customer'
    : 'oms-sidebar-expanded-groups-staff'
}

const LEGACY_SIDEBAR_EXPANDED_STORAGE_KEY = 'oms-sidebar-expanded-groups'

export function loadSidebarExpandedGroups(
  storageKey: string,
  validKeys: ReadonlySet<NavGroupKey>,
): Set<NavGroupKey> {
  const defaultExpanded = DEFAULT_EXPANDED_NAV_GROUPS.filter((key) => validKeys.has(key))
  try {
    let raw = localStorage.getItem(storageKey)
    if (!raw && storageKey === sidebarExpandedStorageKey('staff')) {
      raw = localStorage.getItem(LEGACY_SIDEBAR_EXPANDED_STORAGE_KEY)
      if (raw) {
        localStorage.setItem(storageKey, raw)
        localStorage.removeItem(LEGACY_SIDEBAR_EXPANDED_STORAGE_KEY)
      }
    }
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        const sanitized = parsed.filter(
          (key): key is NavGroupKey => typeof key === 'string' && validKeys.has(key as NavGroupKey),
        )
        if (sanitized.length > 0) return new Set(sanitized)
      }
    }
  } catch {
    /* ignore */
  }
  return new Set(defaultExpanded.length > 0 ? defaultExpanded : [...validKeys].slice(0, 1))
}

export function persistSidebarExpandedGroups(storageKey: string, groups: Set<NavGroupKey>): void {
  localStorage.setItem(storageKey, JSON.stringify([...groups]))
}

/** Desktop-only: whether the labeled sidebar is collapsed (content uses full width). */
export function sidebarDesktopCollapsedStorageKey(portal: 'staff' | 'customer'): string {
  return portal === 'customer'
    ? 'oms-sidebar-desktop-collapsed-customer'
    : 'oms-sidebar-desktop-collapsed-staff'
}

export function loadSidebarDesktopCollapsed(storageKey: string): boolean {
  try {
    return localStorage.getItem(storageKey) === '1'
  } catch {
    return false
  }
}

export function persistSidebarDesktopCollapsed(storageKey: string, collapsed: boolean): void {
  try {
    localStorage.setItem(storageKey, collapsed ? '1' : '0')
  } catch {
    /* ignore */
  }
}

export function navGroupPanelId(groupKey: NavGroupKey): string {
  return `sidebar-nav-group-${groupKey}`
}

export function navItemMatchesPath(item: NavigationItemConfig, pathname: string): boolean {
  if (pathname === item.href) return true
  if (item.href === '/dashboard' || item.href === '/customer') return false
  return pathname.startsWith(`${item.href}/`)
}

export function activeNavGroupForPath(
  items: NavigationItemConfig[],
  pathname: string,
): NavGroupKey | null {
  const matches = items
    .filter((item) => !item.pinned && item.groupKey && navItemMatchesPath(item, pathname))
    .sort((a, b) => b.href.length - a.href.length)
  return matches[0]?.groupKey ?? null
}

function assertNavigationConfig(items: NavigationItemConfig[], label: string) {
  for (const item of items) {
    if (!item.pinned && !item.groupKey && !item.footer) {
      console.error(`[sidebarNavConfig] ${label}: "${item.nameKey}" must be pinned, footer, or have a groupKey`)
    }
  }
}

if (import.meta.env.DEV) {
  assertNavigationConfig(staffNavigationConfig, 'staff')
  assertNavigationConfig(customerNavigationConfig, 'customer')
}
